<template>
    <app-framework>
      <header class="container">
        <bread-crumbs :list="[
          { name: $t('home'), link: '/' },
          { name: $t('role_listing'), link: '/roles' },
          { name: $t($route.params.id === 'new' ? 'role_form_new' : 'role_form_edit') }
        ]" />
        <h1>{{ $t($route.params.id === 'new' ? 'role_form_new' : 'role_form_edit') }}</h1>
      </header>
      <article class="container framework-slot">
        <div v-if="role.loading || rolePermissionsCategories.loading">
            <loading-state/>
        </div>
        <div v-else-if="role.error || rolePermissionsCategories.error" class="alert alert-danger" role="alert">
            {{ $t('failed_retrieve_data_from_server') }}
            <button class="btn btn-link" @click="tryAgain">{{ $t('try_again') }}</button>
        </div>
        <form v-else v-on:submit.prevent="saveRole">
            <div v-if="validationError" class="alert alert-danger" role="alert">
                {{validationError}}
            </div>
            <form-page>
                <div class="form-row">
                    <form-group class="col-12 required" :label="$t('name')" label-for="name" prepend-icon="tag"
                                :invalid-feedback="validationErrors.name">
                        <input id="name" autocomplete="name" type="text" class="form-control"
                               v-model="role.node.name" v-focus>
                    </form-group>
                </div>
                <div class="permissions-container">
                    <label class="form-label">{{$t('permissions')}}</label>
                    <div class="permissions-categories">
                        <div class="form-row" v-for="(permissions, category) in rolePermissionsCategories.nodes"
                             :key="category">
                            <div class="form-group col-12">
                                <label class="permission-category-label">{{$t(category)}}</label>
                                <div class="input-group input-group-permissions">
                                    <div v-for="(permission, index) in permissions" :key="index"
                                         class="custom-control custom-checkbox custom-control-inline">
                                        <input :id="'permission_'+permission" type="checkbox"
                                               class="custom-control-input option-checkbox"
                                               :value="permission" v-model="role.node.permissions">
                                        <label :for="'permission_'+permission" class="custom-control-label">
                                            {{ $t(permission) }}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form-page>
            <footer>
                <div class="form-row">
                    <div class="col-auto">
                        <button :disabled="role.saving" class="btn btn-primary"
                                v-text="!role.saving ? $t('save_button') : $t('saving')"></button>
                    </div>
                </div>
            </footer>
        </form>
      </article>
    </app-framework>
</template>

<style scoped lang="scss">
    .permission-category-label {
        font-size: 0.8rem;
        margin-right: 0.75rem;
        margin-left: 0.75rem;
    }

    .input-group-permissions {
        border-top: 1px dashed #e8e8e8;
        border-bottom: 1px dashed #e8e8e8;
        padding: 0.375rem 0.75rem;
    }

    .permissions-categories {
        padding: 0.375rem 0;
        border-radius: 0.25rem;
        border: 1px solid #ced4da;
        background: #fff;
    }
</style>

<script>
    import FormMixin from "@/mixins/FormMixin";
    import AppFramework from "@/components/layout/AppFramework";
    import FormPage from "@/components/common/FormPage";
    import FormGroup from "@/components/common/FormGroup";
    import LoadingState from "@/components/states/LoadingState";
    import {CREATE_ROLE, ROLE, UPDATE_ROLE, ROLE_PERMISSIONS_CATEGORIES} from "@/gql/Roles";
    import BreadCrumbs from "@/components/layout/BreadCrumbs";

    export default {
        name: 'roleForm',
        i18nOptions: {namespaces: ['role_form', 'role_permissions', 'common', 'breadcrumbs', 'error']},
        components: {BreadCrumbs, LoadingState, FormGroup, FormPage, AppFramework},
        mixins: [FormMixin],
        metaInfo() {
          return {
            title: this.$t(this.$route.params.id === 'new' ? 'role_form_new' : 'role_form_edit')
          }
        },
        data: () => ({
            role: {
                node: null,
                loading: true,
                error: null,
                saving: false
            },
            rolePermissionsCategories: {
                nodes: null,
                loading: true,
                error: null
            }
        }),
        computed: {
            isNew: function () {
                return this.$route.params.id === "new";
            },
        },
        async mounted() {
            await this.queryRolePermissionsCategories();
            await this.queryRole();
        },
        methods: {
            async tryAgain() {
                if (this.rolePermissionsCategories.error) {
                    await this.queryRolePermissionsCategories();
                }
                if (!this.isNew && this.role.error) {
                    await this.queryRole();
                }
            },
            async queryRolePermissionsCategories() {
                this.rolePermissionsCategories.loading = true;
                try {
                    const response = await this.$apolloClient.query({
                        query: ROLE_PERMISSIONS_CATEGORIES,
                    });
                    this.rolePermissionsCategories.nodes = response.data.rolePermissionsCategories;
                    this.rolePermissionsCategories.error = null;
                } catch (e) {
                    if (!e.graphQLErrors || !e.graphQLErrors.some(({extensions, message}) => {
                        if (extensions && extensions.code === "SHOW_ERROR_TO_USER") {
                            this.rolePermissionsCategories.error = message;
                            return true;
                        }
                        return false;
                    })) {
                        this.rolePermissionsCategories.error = "Error to load role permissions";
                    }
                }
                this.rolePermissionsCategories.loading = false;
            },
            async queryRole() {
                if (this.isNew) {
                    this.role.node = {
                        permissions: []
                    };
                    this.role.loading = false;
                    return;
                }

                this.role.loading = true;
                try {
                    const response = await this.$apolloClient.query({
                        query: ROLE,
                        variables: {
                            id: this.$route.params.id
                        },
                        fetchPolicy: "network-only",
                    });
                    this.role.node = response.data.role;

                    this.role.node.permissions = this.role.node.permissions.filter(
                        p => Object.values(this.rolePermissionsCategories.nodes).some(
                            permissions => permissions.some(
                                permission => p === permission
                            )
                        )
                    );

                    this.role.error = null;
                } catch (e) {
                    this.role.error = e.message
                }
                this.role.loading = false;
            },
            async saveRole() {
                if (this.role.saving || !this.checkForm()) {
                    return;
                }
                this.disableForm();
                this.role.saving = true;
                try {
                    if (this.isNew) {
                        await this.createRole();
                    } else {
                        await this.updateRole();
                    }
                    await this.$apolloClient.resetStore();
                    await this.$router.push('/roles');
                } catch (e) {
                    this.role.saving = false;
                    this.activeForm();
                    this.showError(e);
                }
            },
            async createRole() {
                await this.$apolloClient.mutate({
                    mutation: CREATE_ROLE,
                    variables: {
                        input: {
                            name: this.role.node.name,
                            permissions: this.role.node.permissions
                        }
                    }
                });
            },
            async updateRole() {
                await this.$apolloClient.mutate({
                    mutation: UPDATE_ROLE,
                    variables: {
                        id: this.$route.params.id,
                        input: {
                            name: this.role.node.name,
                            permissions: this.role.node.permissions
                        },
                    }
                });
            },
            validateForm() {
                this.validateFormMixin();
            }
        }
    }
</script>
